import { useTranslation } from 'react-i18next';
import type { Key } from 'qampo-ui-library/base';
import type { LocationResponse } from '../../api/generated';
import CheckIcon from '../../assets/icons/check.svg?react';
import { DropdownList } from '../UI/base/DropdownList';
import { LocationColorLine } from './LocationColorLine';
import { resolveLocationColorId } from '../../services/locationColorService';
import styles from './LocationSelector.module.css';

// 12px, kept token-based (base + xs). Shared by the trigger and the menu rows
// so the switcher keeps one padding rhythm.
const SWITCHER_PADDING = 'calc(var(--spacing-base) + var(--spacing-xs))';

// Strips the input-field look from the base trigger so it reads as app context
// (workspace switcher) rather than a form control. Merged over the shared
// DropdownList trigger styles, keeping their border radius.
const TRIGGER_OVERRIDE: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid transparent',
  padding: SWITCHER_PADDING
};

const OPTION_OVERRIDE: React.CSSProperties = {
  padding: SWITCHER_PADDING
};

interface LocationSwitcherViewProps {
  locations: LocationResponse[];
  activeLocationId: string | null;
  isLoading: boolean;
  loadError: string | null;
  onSelect: (locationId: string) => void;
}

// Presentational center switcher: renders the trigger + menu from the locations
// it is given. All data loading and navigation live in the LocationSelector
// container, which keeps this component storybook- and test-friendly.
export const LocationSwitcherView = ({
  locations,
  activeLocationId,
  isLoading,
  loadError,
  onSelect
}: LocationSwitcherViewProps) => {
  const { t } = useTranslation();

  const handleChange = (next: Key[]): void => {
    const nextLocationId = next[0];
    if (nextLocationId === undefined) return;
    onSelect(String(nextLocationId));
  };

  const renderTriggerValue = (selected: LocationResponse[]): React.ReactNode => {
    const active = selected[0];
    return (
      <span className={styles.trigger}>
        {active && <LocationColorLine colorId={resolveLocationColorId(active)} />}
        <span className={styles.triggerText}>
          <span className={styles.triggerCaption}>{t('active-location-label')}</span>
          <span className={styles.triggerName}>{active?.name ?? t('select-location')}</span>
        </span>
      </span>
    );
  };

  const renderLabel = (location: LocationResponse): React.ReactNode => {
    return (
      <span className={styles.option}>
        <LocationColorLine colorId={resolveLocationColorId(location)} />
        <span className={styles.optionName}>{location.name}</span>
        {location.id === activeLocationId && (
          <CheckIcon className={styles.optionCheck} aria-hidden="true" />
        )}
      </span>
    );
  };

  // A load failure leaves nothing to switch between, so surface it in place of
  // the switcher rather than showing a silently-disabled, empty dropdown.
  if (loadError) {
    return (
      <div className={styles.selector}>
        <p className={styles.loadError} role="alert">
          {t('location-load-error')}
        </p>
      </div>
    );
  }

  return (
    <div className={styles.selector}>
      <DropdownList<LocationResponse>
        options={locations}
        getKey={(location) => location.id}
        renderLabel={renderLabel}
        renderTriggerValue={renderTriggerValue}
        value={activeLocationId ? [activeLocationId] : []}
        onChange={handleChange}
        placeholder={t('select-location')}
        disabled={isLoading}
        styles={{ trigger: TRIGGER_OVERRIDE, option: OPTION_OVERRIDE }}
      />
    </div>
  );
};
